import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import Modal from 'react-modal';

import { IconButton, FlatButton, RaisedButton } from 'material-ui';
import { map, filter, get } from 'lodash';

import PostCodes from 'utils/PostCodes';
import genders from 'utils/genders';
import { shouldEdit, shouldNotHide } from 'utils/valuesByKey';
import RootForm from '../form/RootForm';
import Alert from '../Alert';
import * as CRUDActions from '../../actions/EntityCRUD';
import { fetchEmployeeMaster, patchIdentificationCard } from '../../actions/profile';
import { TEXT_FIELD, SELECT_ENUM, PHONE, EMAIL, NUMBER_FIELD } from '../../constants/InputTypes';
import IdentificationCard from '../../components/profile/IdentificationCard';

const childControlers = {}; // must be out of the function scope
let ctl = {};

function validate(values) {
  const errors = {};

  if (values.Email && !/^[-a-z0-9~!$%^&*_=+}{'?]+(\.[-a-z0-9~!$%^&*_=+}{'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i.test(values.Email)) {
    errors.Email = 'Netfang er ekki gilt';
  }

  if (values.PhoneNumberMobile && !/^\+?[0-9,-?,.?,(?,)?]{7,}$/i.test(values.PhoneNumberMobile)) {
    errors.PhoneNumberMobile = 'Símanúmer er ekki gilt';
  }

  return errors;
}

const mapChildFields = child => ({
  fields: [{
    type: TEXT_FIELD,
    label: <FormattedMessage id="profile.child.name" />,
    name: 'ChildName',
    value: child.ChildName,
  }, {
    type: NUMBER_FIELD,
    label: 'Fæðingarár',
    name: 'Birthyear',
    value: child.Birthyear,
  }, {
    type: SELECT_ENUM,
    label: 'Kyn',
    name: 'GenderType',
    value: child.GenderType,
    options: [
      {
        value: 0,
        label: 'Óskilgreint',
      }, {
        value: 1,
        label: 'Karl',
      }, {
        value: 2,
        label: 'Kona',
      },
    ],
  }],
  Entity: child,
});

const EmployeeEditForm = ({
  employeeGeneral, language, valuesByKey, controller, dispatch,
}) => {
  const [open, setOpen] = useState(false);
  const [secondOpen, setSecondOpen] = useState(false);
  const [thirdOpen, setThirdOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [refreshData, setRefreshData] = useState(false);
  const [masterChildren, setMasterChildren] = useState(employeeGeneral.EmployeeMasterChildren || []);
  const [alertText, setAlertText] = useState('');
  const [alertAction, setAlertAction] = useState();
  const [identification, setIdentification] = useState(employeeGeneral.IdentificationCard || '');
  const [changedIdentification, setChangedIdentification] = useState(false);
  const editIdentification = get(valuesByKey, ['Edit.EmployeeMaster.IdentificationCard', 'Value']) === 'true';

  const openDialog = () => setOpen(true);
  const closeDialog = () => setOpen(false);
  const openSecondDialog = () => {
    setSecondOpen(true);
    setRefreshData(false);
  };
  const openThirdDialog = () => {
    setThirdOpen(true);
    setRefreshData(false);
  };
  const closeSecondDialog = () => {
    setOpen(false);
    setSecondOpen(false);
    setAlertOpen(false);
    setMasterChildren(employeeGeneral.EmployeeMasterChildren || []);
    if (refreshData) dispatch(fetchEmployeeMaster());
  };
  const shouldCloseSecond = () => {
    // Check if any of the child forms is dirty
    const oneIsDirty = map(masterChildren, (c, i) => childControlers[i].isDirty()).reduce((x, y) => x || y, false);
    if (oneIsDirty) {
      setAlertOpen(true);
      setAlertText(<FormattedMessage id="profile.alert" />);
      setAlertAction(closeSecondDialog);
    } else {
      closeSecondDialog();
    }
  };

  const onKeyPress = (event) => {
    if (event.keyCode === 27) {
      if (open) {
        closeDialog();
      } else if (secondOpen) {
        shouldCloseSecond();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', onKeyPress);
    controller({
      editProfile: openDialog,
      editFamily: openSecondDialog,
      editRelative: openThirdDialog,
    });

    return () => {
      window.removeEventListener('keydown', onKeyPress);
    };
  });

  const save = async () => {
    if (ctl.isInvalid()) { return; }
    if (changedIdentification) {
      dispatch(patchIdentificationCard(
        { IdentificationCard: identification, EmployeeDetailID: employeeGeneral.EmployeeDetailID },
        employeeGeneral.EmployeeDetailID,
      ));
      setChangedIdentification(false);
    }
    ctl.save();
    closeDialog();
  };

  const closeAlert = () => setAlertOpen(false);

  const addChild = () => {
    const { EmployeeMasterID } = employeeGeneral;
    setMasterChildren([...masterChildren, { EmployeeMasterID }]);
  };

  const removeChild = (EmployeeMasterChild, index) => {
    if (EmployeeMasterChild) {
      dispatch(CRUDActions.remove('EmployeeMasterChild', { ...EmployeeMasterChild }));
    }
    setMasterChildren(filter(masterChildren, (c, i) => i !== index));
    setRefreshData(true);
  };

  const secondSave = (id, i) => {
    if (childControlers[i].isInvalid()) { return; }
    id ? childControlers[i].save() : childControlers[i].create();
  };

  const saveAll = () => {
    map(masterChildren, (c, i) => secondSave(c.EmployeeMasterChildID, i));
    closeSecondDialog();
  };

  const {
    Address,
    PostCode,
    RelativeName,
    RelativeAssociation,
    RelativePhoneNumber,
    RelativeName2,
    RelativeAssociation2,
    RelativePhoneNumber2,
    RelativePhoneNumberMobile,
    RelativePhoneNumberMobile2,
    RelativeEmail,
    RelativeEmail2,
    RelativeAddress,
    RelativeAddress2,
    Email,
    PhoneNumberHome,
    PhoneNumberMobile,
    Entity,
    GenderType,
    PhoneNumberMobileCountryCode,
    Tetra,
  } = employeeGeneral;
  const secondCardActions = [];

  if (shouldEdit(valuesByKey, 'EmployeeMaster')) {
    secondCardActions.push(<RaisedButton
      key="1"
      primary
      onClick={openDialog}
      label={<FormattedMessage id="profile.editProfile" />}
      labelStyle={{ fontSize: 12 }}
    />);
  }

  if (shouldEdit(valuesByKey, 'EmployeeMasterChild') && shouldNotHide(valuesByKey, 'EmployeeMasterChild')) {
    secondCardActions.push(<RaisedButton
      key="2"
      primary
      onClick={openSecondDialog}
      label={<FormattedMessage id="profile.editChildren" />}
      labelStyle={{ fontSize: 12 }}
    />);
  }
  if (shouldEdit(valuesByKey, 'Relative') && shouldNotHide(valuesByKey, 'Relative')) {
    secondCardActions.push(<RaisedButton
      key="3"
      primary
      onClick={openThirdDialog}
      label={<FormattedMessage id="profile.relative" />}
      labelStyle={{ fontSize: 12 }}
    />);
  }

  const actions = [
    <RaisedButton
      label={<FormattedMessage id="alert.cancel" />}
      onClick={closeDialog}
      style={{ marginRight: 8 }}
    />,
    <RaisedButton
      label={<FormattedMessage id="profile.save" />}
      primary
      onClick={save}
    />,
  ];

  const secondActions = [
    <RaisedButton
      label={<FormattedMessage id="alert.cancel" />}
      onClick={closeSecondDialog}
    />, <RaisedButton
      label={<FormattedMessage id="profile.saveclose" />}
      primary
      onClick={saveAll}
      style={{ marginLeft: 8 }}
    />,
  ];
  const thirdActions = [
    <RaisedButton
      label={<FormattedMessage id="alert.cancel" />}
      onClick={() => setThirdOpen(false)}
      style={{ marginRight: 8 }}
    />,
    <RaisedButton
      label={<FormattedMessage id="profile.save" />}
      primary
      onClick={save}
    />,
  ];

  const relativeAssociationOptions = [
    { value: null, label: '' },
    { value: 0, label: <FormattedMessage id="profile.relativeAssociation.unknown" /> },
    { value: 1, label: <FormattedMessage id="profile.relativeAssociation.spouse" /> },
    { value: 2, label: <FormattedMessage id="profile.relativeAssociation.parent" /> },
    { value: 3, label: <FormattedMessage id="profile.relativeAssociation.sibling" /> },
    { value: 4, label: <FormattedMessage id="profile.relativeAssociation.relative" /> },
    { value: 5, label: <FormattedMessage id="profile.relativeAssociation.other" /> },
  ];

  const allFields = [
    {
      type: SELECT_ENUM,
      label: <FormattedMessage id="profile.gender" />,
      name: 'GenderType',
      value: GenderType,
      options: map(Object.entries(genders[language]), ([value, label]) => ({
        value: Number(value),
        label,
      })),
    },
    {
      type: TEXT_FIELD,
      label: <FormattedMessage id="profile.addressStreet" />,
      name: 'Address',
      value: Address,
    },
    {
      type: SELECT_ENUM,
      label: <FormattedMessage id="profile.addressPlace" />,
      name: 'PostCode',
      value: PostCode,
      options: map(Object.entries(PostCodes), ([value, label]) => ({
        value,
        label: `${value} ${label}`,
      })),
    },
    {
      type: EMAIL,
      label: <FormattedMessage id="profile.email" />,
      name: 'Email',
      value: Email,
    },
    {
      type: PHONE,
      label: <FormattedMessage id="profile.countryCode.mobile" />,
      name: 'PhoneNumberMobileCountryCode',
      value: PhoneNumberMobileCountryCode,
    },
    {
      type: PHONE,
      label: <FormattedMessage id="profile.mobile" />,
      name: 'PhoneNumberMobile',
      value: PhoneNumberMobile,
    },
    {
      type: PHONE,
      label: <FormattedMessage id="profile.tetra" />,
      name: 'Tetra',
      value: Tetra,
    },
    {
      type: PHONE,
      label: <FormattedMessage id="profile.phone" />,
      name: 'PhoneNumberHome',
      value: PhoneNumberHome,
    },
  ];
  const relativeFields = [
    {
      type: TEXT_FIELD,
      label: <FormattedMessage id="profile.relativeName" />,
      name: 'RelativeName',
      value: RelativeName,
    },
    {
      type: PHONE,
      label: <FormattedMessage id="profile.relativePhone" />,
      name: 'RelativePhoneNumber',
      value: RelativePhoneNumber,
    },
    {
      type: PHONE,
      label: <FormattedMessage id="profile.relativePhoneMobile" />,
      name: 'RelativePhoneNumberMobile',
      value: RelativePhoneNumberMobile,
    },
    {
      type: SELECT_ENUM,
      label: <FormattedMessage id="profile.relativeRelation" />,
      name: 'RelativeAssociation',
      value: RelativeAssociation,
      options: relativeAssociationOptions,
    },
    {
      type: EMAIL,
      label: <FormattedMessage id="profile.relativeEmail" />,
      name: 'RelativeEmail',
      value: RelativeEmail,
    },
    {
      type: TEXT_FIELD,
      label: <FormattedMessage id="profile.relativeAddress" />,
      name: 'RelativeAddress',
      value: RelativeAddress,
    },
  ];
  const relative2Fields = [
    {
      type: TEXT_FIELD,
      label: <FormattedMessage id="profile.relativeName" />,
      name: 'RelativeName2',
      value: RelativeName2,
    },
    {
      type: PHONE,
      label: <FormattedMessage id="profile.relativePhone" />,
      name: 'RelativePhoneNumber2',
      value: RelativePhoneNumber2,
    },
    {
      type: PHONE,
      label: <FormattedMessage id="profile.relativePhoneMobile" />,
      name: 'RelativePhoneNumberMobile2',
      value: RelativePhoneNumberMobile2,
    },
    {
      type: SELECT_ENUM,
      label: <FormattedMessage id="profile.relativeRelation" />,
      name: 'RelativeAssociation2',
      value: RelativeAssociation2,
      options: relativeAssociationOptions,
    },
    {
      type: EMAIL,
      label: <FormattedMessage id="profile.relativeEmail" />,
      name: 'RelativeEmail2',
      value: RelativeEmail2,
    },
    {
      type: TEXT_FIELD,
      label: <FormattedMessage id="profile.relativeAddress" />,
      name: 'RelativeAddress2',
      value: RelativeAddress2,
    },
  ];

  const filteredFields = filter(allFields, field => ((field.name === 'PhoneNumberMobileCountryCode')
    ? shouldEdit(valuesByKey, 'EmployeeMaster.PhoneNumberMobile')
    : shouldEdit(valuesByKey, `EmployeeMaster.${field.name}`)));

  const childFields = map(masterChildren, mapChildFields);

  const modalStyles = {
    content: {
      top: '10%',
      left: '20%',
      right: '20%',
      padding: 20
    },
  };

  return (
    <div>
      <Modal
        isOpen={open}
        style={modalStyles}
      >
        <div style={{ height: '65vh', minWidth: '50vw', marginBottom: editIdentification === true ? 30 : 0 }}>
          <IconButton
            style={{ margin: -12, float: 'right' }}
            iconClassName="material-icons"
            onClick={closeDialog}
          >
            close
          </IconButton>
          <RootForm
            formName="EmployeeMaster"
            fields={filteredFields}
            initialValues={Entity}
            provideController={(x) => { ctl = x; }}
            dataFetch={fetchEmployeeMaster}
            validate={validate}
          />
          {editIdentification === true && <IdentificationCard
            identification={identification}
            setIdentification={setIdentification}
            setChangedIdentification={setChangedIdentification}
          />}
          <div style={{ marginTop: 20, marginBottom: 20, float: 'right' }}>
            {actions}
          </div>
        </div>
      </Modal>
      <Modal
        style={modalStyles}
        isOpen={secondOpen}
      >
        <div style={{ height: '65vh', minWidth: '50vw' }}>
          <IconButton
            style={{ margin: -12, float: 'right' }}
            iconClassName="material-icons"
            onClick={shouldCloseSecond}
          >
            close
          </IconButton>
          <Alert
            open={alertOpen}
            onOk={alertAction}
            onCancel={closeAlert}
            text={alertText}
          />
          {map(childFields, (c, i) => (
            <div key={i} style={{ marginBottom: 48 }}>
              <RootForm
                formName={`EmployeeMasterChild${i}`}
                entityName="EmployeeMasterChild"
                fields={c.fields}
                initialValues={c.Entity}
                provideController={(x) => { childControlers[i] = x; }}
                dataFetch={childFields.length === i + 1 ? fetchEmployeeMaster : null}
              />
              <FlatButton
                secondary
                label="Eyða"
                onClick={() => removeChild(c.Entity, i)}
                style={{ float: 'right' }}
              />
            </div>
          ))}
          <FlatButton
            label={<FormattedMessage id="profile.add" />}
            primary
            onClick={addChild}
          />
          <div style={{ marginBottom: 20, marginTop: 20, float: 'right' }}>
            {secondActions}
          </div>
        </div>
      </Modal>
      <Modal
        style={modalStyles}
        isOpen={thirdOpen}
      >
        <div style={{ height: '65vh', minWidth: '50vw' }}>
          <IconButton
            style={{ margin: -12, float: 'right' }}
            iconClassName="material-icons"
            onClick={() => setThirdOpen(false)}
          >
            close
          </IconButton>
          <Alert
            open={alertOpen}
            onOk={alertAction}
            onCancel={closeAlert}
            text={alertText}
          />
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            <FormattedMessage id="profile.relative" />
          </div>
          <RootForm
            formName="EmployeeMaster"
            fields={relativeFields}
            initialValues={Entity}
            provideController={(x) => { ctl = x; }}
            dataFetch={fetchEmployeeMaster}
            validate={validate}
          />
          <div style={{ marginTop: 32, fontSize: 16, fontWeight: 600 }}>
            <FormattedMessage id="profile.relative2" />
          </div>
          <RootForm
            formName="EmployeeMaster"
            fields={relative2Fields}
            initialValues={Entity}
            provideController={(x) => { ctl = x; }}
            dataFetch={fetchEmployeeMaster}
            validate={validate}
          />
          <div style={{ marginTop: 20, marginBottom: 20, float: 'right' }}>
            {thirdActions}
          </div>
        </div>
      </Modal>
    </div>
  );
};

EmployeeEditForm.propTypes = {
  employeeGeneral: PropTypes.object.isRequired,
  valuesByKey: PropTypes.object,
  dispatch: PropTypes.func,
  controller: PropTypes.func,
  language: PropTypes.string,
};

const mapStateToProps = ({ languageReducer, profileReducer, WebValuesReducer }) => ({
  language: languageReducer.language,
  employeeGeneral: profileReducer.employeeMaster,
  valuesByKey: WebValuesReducer.valuesByKey,
});

export default connect(mapStateToProps)(EmployeeEditForm);



// WEBPACK FOOTER //
// ./src/components/profile/EmployeeEditForm.js